<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { API } from "@/api/api";
import type {
  TripMonitorItem,
  TripMonitorRoute,
  TripMonitorRouteMap,
  TripMonitorSearchParams,
  TripMonitorTravelOrder,
  TripMonitorTruck,
} from "@/api/trips/dto/trip-monitor";
import type { OrderDto } from "@/api/orders/dto/order.dto";
import { useSnackbarStore } from "@/store/snackbar.store";
import { useDateTime, useFiltersCache, useTrip } from "@/composables";
import { useAppStore } from "@/store/app.store";
import dayjs, { type Dayjs } from "dayjs";
import { useSocketEvent } from "@/composables/useSocketEvent";
import NextRouteStopDialogs from "@/views/trips/monitor/components/NextRouteStopDialogs.vue";
import type { TripUpdate } from "@/api/trips/dto/update";
import BrokerUpdateDialogs from "@/views/trips/monitor/components/BrokerUpdateDialogs.vue";
import LastCheckCallDialogs from "@/views/trips/monitor/components/LastCheckCallDialogs.vue";
import TripsBanner from "@/views/trips/monitor/components/TripsBanner.vue";
import { LoadingRouteStatus, RouteStatus, RouteStopStatus, TripDateField, TripStatus } from "@/constants/trip";
import TripListHeader from "@/views/trips/monitor/components/TripListHeader.vue";
import RedispatchConfirmationDialog from "@/views/trips/components/dialogs/RedispatchConfirmationDialog.vue";
import type { TripRouteDto } from "@/api/trips/dto/trip.dto";
import TripItem from "@/views/trips/monitor/components/TripItem.vue";
import { TripMonitorHiddenRouteStatuses } from "@/constants/route";
import { OrderStopType } from "@/constants/order";
import TripsSort from "@/views/trips/monitor/components/TripsSort.vue";

const searchParamsKey = "trip-monitor";
const animationDuration = 3000;

const tripsApi = new API().trips;

const props = defineProps<{
  searchParams: TripMonitorSearchParams | null;
}>();

const { dateTZ, dateToUTC } = useDateTime();
const { dispatcher } = useAppStore();
const { setSearchParams, getSearchParams } = useFiltersCache();
const { showSnackbarError } = useSnackbarStore();
const { completeFormTrips, sortTripsByDate } = useTrip();

const trips = ref<TripMonitorItem[]>([]);
const updatedRouteIds = ref<number[]>([]);
const isLoading = ref<boolean>(false);
const selectedTripId = ref<number | null>(null);
const selectedTripItem = ref<TripMonitorItem | null>(null);
const selectedTripMonitorRoute = ref<TripMonitorRoute | null>(null);
const selectedTripMonitorTruck = ref<TripMonitorTruck | null>(null);
const selectedRoute = ref<TripRouteDto | null>(null);
const selectedTravelOrder = ref<TripMonitorTravelOrder | null>(null);
const showActionDialog = ref<boolean>(false);
const showRouteInfoDialog = ref<boolean>(false);
const actionDialogLoading = ref<boolean>(false);
const showCreateBrokerUpdateDialog = ref<boolean>(false);
const showBrokerUpdateInfoDialog = ref<boolean>(false);
const brokerUpdate = ref<TripUpdate | null>(null);
const showAddCheckCallDialog = ref<boolean>(false);
const showConfirmDispatchDialog = ref<boolean>(false);
const showCheckCallIntervalDialog = ref<boolean>(false);
const showBrokerUpdateIntervalDialog = ref<boolean>(false);
const showRedispatchDialog = ref<boolean>(false);
const tripsFromSocket = ref<TripMonitorItem[]>([]);
const tripsLoadedData = ref<Dayjs>(dayjs());
const sortOption = ref<TripDateField>(TripDateField.NextStopSoonest);
const showPlannedRoutes = ref<boolean>(false);
const listKey = ref<number>(0);

const filteredSortedTrips = computed<TripMonitorItem[]>(() => {
  if (!props.searchParams) {
    return trips.value;
  }

  const {
    refNumber,
    truckNumber,
    billToCompanyId,
    progresses,
    orderNumber,
    travelOrderId,
    arrivalDate,
    bookedBy,
    dispatchStatus,
    truckType,
  } = props.searchParams;

  const filteredTrips = [...trips.value].filter((item) => {
    let matches = true;

    if (refNumber) {
      matches &&= item.orders.some((order) => order.refNumbers.includes(refNumber.toLowerCase().trim()));
    }

    if (bookedBy?.length) {
      matches &&= item.orders.some((order) => bookedBy.includes(order.bookedBy));
    }

    if (truckNumber) {
      matches &&= item.routes.some(
        (route) => route.truck.number.toLowerCase().trim() === truckNumber.toLowerCase().trim(),
      );
    }

    if (billToCompanyId) {
      matches &&= item.orders.some((order) => order.customer.id === billToCompanyId);
    }

    if (progresses && progresses.length) {
      const matchNextStopStatus = item.routes.some((route) => {
        return route.nextRouteStop && progresses.includes(route.nextRouteStop.status);
      });

      const matchNextStopType = item.routes.some((route) => {
        const necessaryStatuses = [RouteStopStatus.inRoute, RouteStopStatus.notStarted];

        return (
          route.nextRouteStop &&
          necessaryStatuses.includes(route.nextRouteStop.status) &&
          progresses.includes(route.nextRouteStop.type)
        );
      });

      const matchUnloading =
        progresses.includes(LoadingRouteStatus.unloading) &&
        item.routes.some((route) => {
          return (
            route.nextRouteStop &&
            route.nextRouteStop.type === OrderStopType.delivery &&
            route.nextRouteStop.status === RouteStopStatus.waitingForLoading
          );
        });

      const matchLoading =
        progresses.includes(LoadingRouteStatus.loading) &&
        item.routes.some((route) => {
          return (
            route.nextRouteStop &&
            route.nextRouteStop.type === OrderStopType.pickup &&
            route.nextRouteStop.status === RouteStopStatus.waitingForLoading
          );
        });

      matches &&= matchNextStopStatus || matchNextStopType || matchUnloading || matchLoading;
    }

    if (orderNumber) {
      matches &&= item.orders.some((order) => order.number === Number(orderNumber));
    }

    if (truckType?.length) {
      matches &&= item.routes.some((route) => truckType.includes(route.truck.type));
    }

    if (travelOrderId) {
      matches &&= item.routes.some((route) =>
        route.travelOrders.some((travelOrder) => travelOrder.id === Number(travelOrderId)),
      );
    }

    if (dispatchStatus) {
      if (dispatchStatus === "not-confirmed-dispatch") {
        matches &&= item.routes.some((route) => {
          return !route.dispatchConfirmed;
        });
      } else {
        matches &&= item.routes.some((route) => {
          return route.dispatchConfirmed && route.redispatch === dispatchStatus;
        });
      }
    }

    if (arrivalDate) {
      matches &&= item.routes.some((route) => {
        const nextStopTime = route.nextRouteStop?.timeTo;

        if (!route.nextRouteStop || !nextStopTime || !dispatcher?.timezone) {
          return true;
        }

        const nextStopTimeInUTC = dateToUTC(nextStopTime, route.nextRouteStop.timezone).toString();
        const formattedNextRouteTime = dateTZ(nextStopTimeInUTC, dispatcher?.timezone, "YYYY-MM-DD");

        return dayjs(formattedNextRouteTime).isBefore(arrivalDate) || dayjs(formattedNextRouteTime).isSame(arrivalDate);
      });
    }

    if (!showPlannedRoutes.value) {
      matches &&= item.routes.every((route) => {
        return route.status !== RouteStatus.planned;
      });
    }

    return matches;
  });

  return sortTrips(filteredTrips);
});

const routeByTruckId = computed<TripMonitorRouteMap>(() => {
  const routes = filteredSortedTrips.value.flatMap((trip) => trip.routes);

  return routes.reduce((acc: TripMonitorRouteMap, route) => {
    const truckId = route.truck.id;

    if (acc[truckId]) {
      acc[truckId].push(route);
    } else {
      acc[truckId] = [route];
    }

    return acc;
  }, {});
});

function setTrips(tripsFromAPI: TripMonitorItem[]) {
  trips.value = completeFormTrips(tripsFromAPI);
}

async function loadTripMonitor(showSkeleton = false) {
  isLoading.value = showSkeleton;
  tripsFromSocket.value = [];

  try {
    const response = await tripsApi.fetchTripMonitor();
    if (response.success && response.data?.trips) {
      setTrips(response.data.trips);
      tripsLoadedData.value = dayjs();
    }
  } catch (error) {
    showSnackbarError("Could not load trip monitor");
  } finally {
    isLoading.value = false;
  }
}

function setShowPlannedByDefault() {
  const filters = getSearchParams(searchParamsKey);

  showPlannedRoutes.value = Boolean(filters?.showPlannedRoutes);
}

function refreshTrips() {
  const existingTripsMap = new Map(trips.value.map((trip) => [trip.id, trip]));

  for (const trip of tripsFromSocket.value) {
    existingTripsMap.set(trip.id, trip);
  }

  const updatedTrips = Array.from(existingTripsMap.values()).filter((trip) => trip.status !== TripStatus.completed);
  setTrips(updatedTrips);
  listKey.value++;
  tripsFromSocket.value = [];
  tripsLoadedData.value = dayjs();
}

function handleNewTrip(newTrip: TripMonitorItem) {
  const hasTrip = trips.value.some((item) => item.id === newTrip.id);

  if (hasTrip) {
    trips.value = trips.value.map((existedTrip) => {
      if (existedTrip.id === newTrip.id) {
        return newTrip;
      }

      return existedTrip;
    });

    return;
  }

  tripsFromSocket.value.push(newTrip);
}

async function setUpdateRouteId(id: number) {
  await nextTick();

  if (!updatedRouteIds.value.includes(id)) {
    updatedRouteIds.value.push(id);

    setTimeout(() => {
      updatedRouteIds.value = updatedRouteIds.value.filter((routeId) => routeId !== id);
    }, animationDuration);
  }
}

function updateTripRoute(
  tripList: TripMonitorItem[],
  route: TripMonitorRoute,
  needAnimation = true,
): TripMonitorItem[] {
  return tripList.map((trip) => {
    const relatedRoute = trip.routes.find((r) => r.id === route.id);
    if (!relatedRoute) {
      return trip;
    }

    if (needAnimation) {
      setUpdateRouteId(relatedRoute.id);
    }

    const updatedRoutes = trip.routes.map((r) => (r.id === route.id ? route : r));

    const isAllRouteCompletedOrCancelled = updatedRoutes.every((r) =>
      TripMonitorHiddenRouteStatuses.includes(r.status),
    );

    const tripStatus = isAllRouteCompletedOrCancelled ? TripStatus.completed : trip.status;

    return {
      ...trip,
      status: tripStatus,
      routes: updatedRoutes,
    };
  });
}

function handleTripRouteUpdate(route: TripMonitorRoute) {
  trips.value = updateTripRoute([...trips.value], route);
  tripsFromSocket.value = updateTripRoute([...tripsFromSocket.value], route, false);

  const updatedTrip = trips.value.find((t) => t.routes.some((r) => r.id === route.id));
  if (!updatedTrip) {
    return;
  }

  const isAllRouteCompletedOrCancelled = updatedTrip.routes.every((r) =>
    TripMonitorHiddenRouteStatuses.includes(r.status),
  );

  if (isAllRouteCompletedOrCancelled) {
    tripsFromSocket.value.push({ ...updatedTrip, status: TripStatus.completed });
  }
}

function updateTripTruck(tripList: TripMonitorItem[], updatedTruck: TripMonitorTruck, needAnimation = true) {
  return tripList.map((trip) => {
    const relatedRoute = trip.routes.find((route) => route.truck.id === updatedTruck.id);

    if (!relatedRoute) {
      return trip;
    }

    if (needAnimation) {
      setUpdateRouteId(relatedRoute.id);
    }

    return {
      ...trip,
      routes: trip.routes.map((route) => {
        if (route.id === relatedRoute.id) {
          return {
            ...route,
            truck: updatedTruck,
          };
        }

        return route;
      }),
    };
  });
}

function updateTripOrder(tripList: TripMonitorItem[], updatedOrder: OrderDto): TripMonitorItem[] {
  return tripList.map((trip) => {
    const hasOrder = trip.orders.some((order) => order.id === updatedOrder.id);

    if (!hasOrder) {
      return trip;
    }

    return {
      ...trip,
      orders: trip.orders.map((order) => {
        if (order.id === updatedOrder.id) {
          return {
            ...order,
            generalNote: updatedOrder.generalNote ?? "",
            orderStops: order.orderStops.map((orderStop) => {
              const updatedStop = updatedOrder.orderStops.find((stop) => stop.id === orderStop.id);

              if (!updatedStop) {
                return orderStop;
              }

              return {
                ...orderStop,
                note: updatedStop.note ?? "",
              };
            }),
          };
        }

        return order;
      }),
    };
  });
}

function handleTripOrderUpdate(updatedOrder: OrderDto) {
  if (!updatedOrder.id) {
    return;
  }

  trips.value = updateTripOrder([...trips.value], updatedOrder);
  tripsFromSocket.value = updateTripOrder([...tripsFromSocket.value], updatedOrder);
}

function handleTripTruckUpdate(updatedTruck: TripMonitorTruck) {
  trips.value = updateTripTruck([...trips.value], updatedTruck);
  tripsFromSocket.value = updateTripTruck([...tripsFromSocket.value], updatedTruck, false);
}

function setRouteDialogData(tripId: number, route: TripMonitorRoute) {
  selectedTripId.value = tripId;
  selectedTripMonitorRoute.value = route;
}

function setBrokerUpdateDialogData(
  route: TripMonitorRoute,
  travelOrder: TripMonitorTravelOrder | null,
  trip: TripMonitorItem,
  update: TripUpdate | null = null,
) {
  brokerUpdate.value = update;
  selectedTripMonitorRoute.value = route;
  selectedTravelOrder.value = travelOrder;
  selectedTripItem.value = trip;
  selectedTripId.value = trip.id;
}

function setLastCheckCallDialogsData(route: TripMonitorRoute, trip: TripMonitorItem) {
  selectedTripMonitorRoute.value = route;
  selectedTripItem.value = trip;
  selectedTripId.value = trip.id;
}

function setRedispatchDialogData(route: TripRouteDto, truck: TripMonitorTruck, tripId: number) {
  selectedRoute.value = route;
  selectedTripMonitorTruck.value = truck;
  selectedTripId.value = tripId;
}

function sortTrips(trips: TripMonitorItem[]): TripMonitorItem[] {
  const isSoonest = [
    TripDateField.NextStopSoonest,
    TripDateField.ETASoonest,
    TripDateField.LastCheckCallSoonest,
    TripDateField.CheckCallTimersSoonest,
    TripDateField.UpdateTimerSoonest,
  ].includes(sortOption.value);

  const sortMode: "soonest" | "latest" = isSoonest ? "soonest" : "latest";

  return sortTripsByDate(trips, sortOption.value, sortMode);
}

useSocketEvent("trip:monitor-item", ({ trip }) => {
  handleNewTrip(trip);
});

useSocketEvent("trip:route-updated", ({ route }) => {
  handleTripRouteUpdate(route);
});

useSocketEvent("trip:truck-updated", ({ truck }) => {
  handleTripTruckUpdate(truck);
});

useSocketEvent("order:updated", ({ order }) => {
  handleTripOrderUpdate(order);
});

watch(showPlannedRoutes, (value) => {
  const filters = getSearchParams(searchParamsKey);
  setSearchParams(searchParamsKey, {
    ...filters,
    showPlannedRoutes: value,
  });
});

onMounted(async () => {
  await loadTripMonitor(true);
  setShowPlannedByDefault();
});
</script>

<template>
  <div class="font-weight-500 text-grey-darken-1 mb-4 d-flex align-center justify-space-between">
    <div class="d-flex align-center">
      <span>{{ filteredSortedTrips.length }} results found</span>

      <v-checkbox
        v-model="showPlannedRoutes"
        class="font-size-14 text-grey-darken-2 ml-4"
        color="primary"
        density="compact"
        hide-details
      >
        <template #label><span class="font-size-14">Show trips with planned routes</span></template>
      </v-checkbox>

      <trips-banner
        v-if="!isLoading"
        v-model:sort-option="sortOption"
        :new-trips="tripsFromSocket"
        :trips-loaded-data="tripsLoadedData"
        class="ml-3"
        @refresh="refreshTrips"
      ></trips-banner>
    </div>

    <trips-sort v-model="sortOption" />
  </div>
  <div class="trips-list">
    <trip-list-header />

    <v-virtual-scroll v-if="!isLoading" :key="listKey" :items="filteredSortedTrips">
      <template #default="{ item }">
        <trip-item
          :key="item.id"
          v-model:action-dialog-loading="actionDialogLoading"
          v-model:show-action-dialog="showActionDialog"
          v-model:show-add-check-call-dialog="showAddCheckCallDialog"
          v-model:show-broker-update-info-dialog="showBrokerUpdateInfoDialog"
          v-model:show-broker-update-interval-dialog="showBrokerUpdateIntervalDialog"
          v-model:show-check-call-interval-dialog="showCheckCallIntervalDialog"
          v-model:show-confirm-dispatch-dialog="showConfirmDispatchDialog"
          v-model:show-create-broker-update-dialog="showCreateBrokerUpdateDialog"
          v-model:show-redispatch-dialog="showRedispatchDialog"
          v-model:show-route-info-dialog="showRouteInfoDialog"
          :routes-by-truck-id="routeByTruckId"
          :selected-trip-monitor-route="selectedTripMonitorRoute"
          :trip="item"
          :updated-route-ids="updatedRouteIds"
          @set-broker-update-dialog-data="setBrokerUpdateDialogData"
          @set-last-check-call-dialogs-data="setLastCheckCallDialogsData"
          @set-redispatch-dialog-data="setRedispatchDialogData"
          @set-route-dialog-data="setRouteDialogData"
        />
      </template>
    </v-virtual-scroll>

    <div v-else class="mb-4 trips-list__skeleton">
      <v-skeleton-loader v-for="item in 8" :key="item" :loading="true" class="mb-4" height="220px" width="100%">
        <v-sheet color="grey-lighten-3" height="220px" rounded width="100%"></v-sheet>
      </v-skeleton-loader>
    </div>

    <next-route-stop-dialogs
      v-if="selectedTripMonitorRoute && selectedTripId"
      v-model:loading="actionDialogLoading"
      v-model:show-action-dialog="showActionDialog"
      v-model:show-info-dialog="showRouteInfoDialog"
      :trip-id="selectedTripId"
      :trip-monitor-route="selectedTripMonitorRoute"
    />

    <broker-update-dialogs
      v-model:show-create-dialog="showCreateBrokerUpdateDialog"
      v-model:show-info-dialog="showBrokerUpdateInfoDialog"
      v-model:show-interval-dialog="showBrokerUpdateIntervalDialog"
      :broker-update="brokerUpdate"
      :travel-order="selectedTravelOrder"
      :trip="selectedTripItem"
      :trip-monitor-route="selectedTripMonitorRoute"
    />

    <last-check-call-dialogs
      v-if="selectedTripMonitorRoute && selectedTripItem"
      v-model:show-add-check-call-dialog="showAddCheckCallDialog"
      v-model:show-confirm-dispatch-dialog="showConfirmDispatchDialog"
      v-model:show-interval-dialog="showCheckCallIntervalDialog"
      :trip="selectedTripItem"
      :trip-monitor-route="selectedTripMonitorRoute"
    />

    <redispatch-confirmation-dialog
      v-if="selectedTripId && selectedRoute && showRedispatchDialog"
      v-model="showRedispatchDialog"
      :route="selectedRoute"
      :trip-id="selectedTripId"
      :truck="selectedTripMonitorTruck"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trips-list {
  &:deep(.v-btn__content) {
    text-transform: capitalize;
  }
}

.trip-list-enter-active,
.trip-list-leave-active {
  transition: all 0.5s ease;
}

.trip-list-enter-from,
.trip-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.trips-list {
  display: grid;
  grid-template-columns: repeat(6, auto);

  &__skeleton {
    grid-column: 1 / -1;
  }
}
</style>

<style>
.v-expansion-panels,
.v-virtual-scroll,
.v-virtual-scroll__container,
.v-virtual-scroll__item {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.v-virtual-scroll__item {
  height: max-content;
}

.v-virtual-scroll__container {
  height: max-content;
}
</style>
